import {
  BufferGeometry, CanvasTexture, DirectionalLight, Euler, Group, HemisphereLight,
  Mesh, MeshBasicMaterial, MeshStandardMaterial, Quaternion, ShaderMaterial, SRGBColorSpace, Texture, Vector3,
} from 'three';
import type { Flight } from '../universe/Flight';
import { disposeObjectTree } from '../core/dispose';
import { buildCabin } from './CockpitGeometry';
import { DISPLAY_HEIGHT, DISPLAY_WIDTH, drawFlightDisplay } from './FlightDisplay';

/** The pilot sits behind a single integrated console inside a survey vessel. */
export class Cockpit {
  readonly root = new Group();
  private body = new Group();
  private shell = new Group();
  private canvas: HTMLCanvasElement;
  private texture: CanvasTexture;
  private screen: Mesh<BufferGeometry, MeshBasicMaterial>;
  private previousRotation = new Quaternion();
  private turn = new Euler(0, 0, 0, 'YXZ');
  private delta = new Quaternion();
  private glow?: MeshStandardMaterial;
  private windshield?: ShaderMaterial;
  private stick = new Group();
  private throttle = new Group();
  private initialized = false;
  private panelTime = 1;
  private compact = false;
  private bank = 0;
  private sway = new Vector3();
  private wantedSway = new Vector3();
  private sun = new DirectionalLight('#f0e9db', 2.3);
  private lightDirection = new Vector3();
  private inverseAttitude = new Quaternion();
  private width = 0;
  private height = 0;

  constructor() {
    this.root.name = 'AURORA / deep-space survey cockpit'; this.root.add(this.body);
    this.canvas = document.createElement('canvas'); this.canvas.width = DISPLAY_WIDTH; this.canvas.height = DISPLAY_HEIGHT;
    this.texture = new CanvasTexture(this.canvas); this.texture.colorSpace = SRGBColorSpace;
    this.screen = new Mesh(new BufferGeometry(), new MeshBasicMaterial({ map: this.texture, toneMapped: false }));
    this.screen.name = 'flight_display'; this.screen.frustumCulled = false;
    this.setViewport(1280, 720, 55);
    this.root.add(new HemisphereLight('#b1ccd7', '#0a1016', 1.05));
    this.sun.name = 'Exterior sunlight'; this.sun.target.position.set(0, -.5, -3); this.root.add(this.sun, this.sun.target);
    const rim = new DirectionalLight('#91c2c5', .6); rim.position.set(2, -.4, 0); rim.target.position.set(0, -1, -3); this.root.add(rim, rim.target);
  }
  setViewport(width: number, height: number, fov: number) {
    if (width !== this.width || height !== this.height) {
      const next = buildCabin(width, height);
      // Only replace the plane. The already-uploaded CanvasTexture retains
      // exactly the same dimensions through phone orientation changes.
      this.screen.removeFromParent(); this.releaseWindshieldInputs();
      this.body.remove(this.shell); disposeObjectTree(this.shell);
      this.shell = next.root; this.body.add(this.shell); this.compact = next.compact;
      this.glow = next.glow; this.windshield = next.windshield; this.stick = next.stick; this.throttle = next.throttle;
      this.screen.geometry.dispose(); this.screen.geometry = next.panel.geometry;
      this.screen.userData.samples = next.panel.samples.map(point => point.toArray()); this.shell.add(this.screen);
      this.width = width; this.height = height; this.panelTime = 1;
    }
    const halfHeight = Math.tan(fov * Math.PI / 360); this.shell.scale.set(halfHeight, halfHeight, 1);
    this.root.updateMatrixWorld(true);
  }
  setExterior(texture: Texture, width: number, height: number) {
    if (!this.windshield) return;
    this.windshield.uniforms.uExterior.value = texture; this.windshield.uniforms.uResolution.value.set(width, height);
  }
  releaseWindshieldInputs() {
    if (this.windshield) this.windshield.uniforms.uExterior.value = null;
  }
  update(flight: Flight, dt: number, reduced: boolean) {
    if (!this.initialized) { this.previousRotation.copy(flight.rotation); this.initialized = true; }
    this.delta.copy(this.previousRotation).invert().multiply(flight.rotation); this.turn.setFromQuaternion(this.delta, 'YXZ');
    const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));
    const wanted = reduced || !dt ? 0 : clamp(this.turn.y / dt * .020, .022);
    this.bank = reduced ? 0 : this.bank + (wanted - this.bank) * (1 - Math.exp(-dt * 8));
    const yaw = reduced || !dt ? 0 : clamp(-this.turn.y / dt * .012, .017);
    const pitch = reduced || !dt ? 0 : clamp(-this.turn.x / dt * .012, .012);
    if (reduced) this.sway.set(0, 0, 0);
    else this.sway.lerp(this.wantedSway.set(pitch, yaw, Math.min(.020, flight.speed / 45000)), 1 - Math.exp(-dt * 7));
    this.body.rotation.set(this.sway.x, this.sway.y, this.bank);
    this.body.position.set(this.sway.y * 1.5, -this.sway.x * .85, this.sway.z);
    // Physical controls follow real turning and propulsion, without changing aim.
    this.stick.rotation.set(reduced ? 0 : this.sway.x * 14, 0, reduced ? 0 : this.bank * 9);
    const thrust = -Math.min(.20, flight.speed / 2500);
    this.throttle.position.z = reduced ? thrust : this.throttle.position.z + (thrust - this.throttle.position.z) * (1 - Math.exp(-dt * 7));
    this.previousRotation.copy(flight.rotation);
    this.inverseAttitude.copy(flight.rotation).invert();
    this.lightDirection.set(-.8, .45, .7).normalize().applyQuaternion(this.inverseAttitude);
    this.sun.position.copy(this.sun.target.position).addScaledVector(this.lightDirection, 7);
    if (this.glow) this.glow.emissiveIntensity = 6.3 + Math.min(.12, flight.speed / 3500);
    if (this.windshield) this.windshield.uniforms.uSunDirection.value.copy(this.lightDirection);
    this.panelTime += dt;
    if (this.panelTime >= .1) {
      this.panelTime = 0; const context = this.canvas.getContext('2d');
      if (context) { drawFlightDisplay(context, flight); this.texture.needsUpdate = true; }
    }
  }
  get stats() { return {
    cockpitVisible: this.root.visible, cockpitBank: Number(this.bank.toFixed(4)),
    cockpitSway: this.sway.toArray().map(value => Number(value.toFixed(4))), cockpitCompact: this.compact,
    cockpitModel: 'procedural', cockpitLayout: 'survey-vessel', cockpitGlazing: Boolean(this.windshield),
    cockpitDisplays: 1, cockpitHandControls: 2,
  }; }
}
